<template>
  <full-screen :title="title" @close="cancel">
    <transition name="forward">
      <div
        v-if="!showEmployeeTill"
        class="w-full h-full flex flex-col items-center py-4 sm:py-8 px-4"
      >
        <div class="flex-1 min-h-0 flex flex-col items-center max-w-[33.5rem] w-full">
          <cash-amount
            v-model="cashTillAmount"
            class="w-full flex-1 min-h-0"
            :method-name="startShiftMethod"
          >
            <div class="flex justify-end flex-col gap-2 shrink-0 mt-4">
              <l-button
                class="w-full"
                @click="startShiftAndContinue(cashTillAmount, selectedCashTill!.id)"
              >
                {{ t('start-shift.start') }}
              </l-button>
              <div v-if="lastShiftCashTillAmount" class="flex gap-2">
                <l-button type="secondary" class="w-full" @click="printZReport()">
                  {{ t('cash-amount.print-last-z-report') }}
                </l-button>
                <l-button
                  v-if="lastShiftCashTillAmount != null && !useCashMachine"
                  type="secondary"
                  class="w-full"
                  :disabled="lastShiftCashTillAmount === 0"
                  @click="cashTillAmount = lastShiftCashTillAmount!"
                >
                  {{ t('cash-amount.copy-last') }}:
                  {{ currency(lastShiftCashTillAmount) }}
                </l-button>
              </div>
            </div>
          </cash-amount>
        </div>
      </div>
      <div
        v-else-if="showEmployeeTill && !employeeShiftStarted"
        class="w-full h-full flex flex-col items-center py-4 sm:py-8 px-4"
      >
        <div class="flex-1 min-h-0 flex flex-col items-center max-w-[33.5rem] w-full">
          <cash-amount v-model="employeeTillAmount" class="w-full flex-1 min-h-0">
            <div class="flex justify-end flex-col gap-2 shrink-0 mt-4">
              <l-button
                class="w-full"
                @click="startShiftAndContinue(employeeTillAmount, currentEmployee!.tillId!)"
              >
                {{ t('start-shift.start') }}
              </l-button>
              <div v-if="lastShiftEmployeeTillAmount" class="flex gap-2">
                <l-button type="secondary" class="w-full" @click="printZReport()">
                  {{ t('cash-amount.print-last-z-report') }}
                </l-button>
                <l-button
                  v-if="lastShiftEmployeeTillAmount != null"
                  type="secondary"
                  class="w-full"
                  :disabled="lastShiftEmployeeTillAmount === 0"
                  @click="employeeTillAmount = lastShiftEmployeeTillAmount!"
                >
                  {{ t('cash-amount.copy-last') }}:
                  {{ currency(lastShiftEmployeeTillAmount) }}
                </l-button>
              </div>
            </div>
          </cash-amount>
        </div>
      </div>
    </transition>
  </full-screen>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton } from '@last/core-ui/paprika'

import { getLastShift } from '@/api/shift'
import FullScreen from '@/components/FullScreen.vue'
import { useMoney } from '@/composables/useMoney'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import ticketPrinter from '@/integrations/printer/ticketPrinter'
import { useAuthStore } from '@/store/auth'
import { useTillStore } from '@/store/till'
import type { LastShift } from '@/types'

import CashAmount from './CashAmount.vue'

const tillStore = useTillStore()
const authStore = useAuthStore()
const router = useRouter()
const { t } = useI18n()

const { startShift } = tillStore
const { selectedCashTill, selectedCashTillIsStarted, tillIsStarted } = storeToRefs(tillStore)
const { currentEmployee } = storeToRefs(authStore)
const { currency } = useMoney()

const cashTillAmount = ref(0)
const employeeTillAmount = ref(0)
const showEmployeeTill = ref(false)
const lastShiftCashTill = ref<LastShift>()
const lastShiftEmployeeTill = ref<LastShift>()

const useCashMachine = computed(() => {
  return CashMachine.isEnabled()
})

const startShiftMethod = computed(() => CashMachine.method || 'cash')

const title = computed(() => {
  const showSteps = !!selectedCashTill.value && !!showEmployeeTill.value
  return !showEmployeeTill.value
    ? `${t('start-shift.title')} ${selectedCashTill.value!.name}${showSteps ? ' (1/2)' : ''}`
    : `${t('start-shift.title')} ${currentEmployee.value!.name}${showSteps ? ' (2/2)' : ''}`
})

const employeeShiftStarted = computed(() => {
  return !currentEmployee.value?.tillEnabled || tillIsStarted.value(currentEmployee.value.tillId!)
})

const allShiftsStarted = computed(() => {
  return (
    employeeShiftStarted.value && !!(!selectedCashTill.value || selectedCashTillIsStarted.value)
  )
})

const lastShiftCashTillAmount = computed(() => {
  if (!lastShiftCashTill.value || !lastShiftCashTill.value.amounts) {
    return null
  } else {
    const cashAmount = lastShiftCashTill.value.amounts.find(amount => amount.method === 'cash')
    if (cashAmount) {
      return cashAmount.end
    }
  }
  return null
})

const lastShiftEmployeeTillAmount = computed(() => {
  if (!lastShiftEmployeeTill.value || !lastShiftEmployeeTill.value.amounts) {
    return null
  } else {
    const cashAmount = lastShiftEmployeeTill.value.amounts.find(amount => amount.method === 'cash')
    if (cashAmount) {
      return cashAmount.end
    }
  }
  return null
})

onMounted(async () => {
  if (allShiftsStarted.value) {
    router.push({ name: 'pos' })
  }
  if (selectedCashTill.value && selectedCashTill.value.id) {
    lastShiftCashTill.value = await getLastShift(selectedCashTill.value.id)
  }
  if (currentEmployee.value?.tillEnabled && currentEmployee.value?.tillId) {
    lastShiftEmployeeTill.value = await getLastShift(currentEmployee.value.tillId)
  }
})

onBeforeMount(() => {
  if (!selectedCashTill.value || selectedCashTillIsStarted.value) {
    showEmployeeTill.value = true
  }
})

function startShiftAndContinue(amount: number, tillId: string) {
  const method = CashMachine.method || 'cash'
  startShift({ amount, tillId, method })
  showEmployeeTill.value = true
}

function cancel() {
  router.push({ name: 'employees' })
}

function printZReport() {
  ticketPrinter.printZReport()
}

watch(allShiftsStarted, started => {
  if (started) {
    router.push({ name: 'pos' })
  }
})
</script>

<style scoped>
.forward-enter-active,
.forward-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: transform 0.3s ease;
}

.forward-leave-to {
  transform: translateX(-100%);
}

.forward-enter-from {
  transform: translateX(100%);
}
</style>
